import { useCallback, useMemo, useState, FC, MouseEvent } from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import eventAvailable from '@iconify/icons-ic/outline-event-available';
import moreVertFill from '@iconify/icons-eva/more-vertical-fill';
import closeCircleOutline from '@iconify/icons-eva/close-circle-outline';
import refreshFill from '@iconify/icons-eva/refresh-fill';
import checkmarkOutline from '@iconify/icons-eva/checkmark-circle-2-outline';
import {
  Appointment,
  APPOINTMENT_STATUS_LABELS,
  COMM_KIND_LABELS,
  UnitOption,
} from './types';
import CommsPanel from './CommsPanel';
import { unitsSummary } from './unitLabels';
import { AppointmentActionBar } from './AppointmentRow';

export const appointmentUnitLabels = (
  units: UnitOption[] | undefined
): string[] => (units ?? []).map((unit) => unit.label ?? unit.name);

/** Unit numbers for one appointment, property named at most once.
 *
 * Cards default to naming the property once (they travel — cert file, Manage
 * dialog, calendar popovers), where a bare "4B" can be ambiguous. List rows pass
 * ``false`` because their surface already shows the property in its own column
 * or in the page header.
 */
export const AppointmentUnits: FC<{
  units: UnitOption[] | undefined;
  showProperty?: boolean;
}> = ({ units, showProperty = true }) => {
  const summary = unitsSummary(units, { showProperty });
  if (!summary) {
    return null;
  }
  return (
    <Tooltip
      title={summary.tooltip}
      placement="top-start"
      sx={{ whiteSpace: 'pre-line' }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {summary.text}
        </Typography>
        {summary.hidden > 0 && (
          <Chip
            size="small"
            variant="outlined"
            label={`+${summary.hidden}`}
          />
        )}
      </Stack>
    </Tooltip>
  );
};

interface AppointmentCardProps {
  appointment: Appointment;
  onReschedule: (appointment: Appointment) => void;
  onCancel: (appointment: Appointment) => void;
  onOutcome: (
    appointment: Appointment,
    outcome: 'completed' | 'no_show'
  ) => void;
  onViewConversation: (conversationId: number) => void;
  /** When true, actions render inline instead of behind a kebab menu. */
  inlineActions?: boolean;
}

const statusChipColor = (
  status: Appointment['status']
): 'success' | 'default' | 'error' | 'warning' | 'info' => {
  switch (status) {
    case 'scheduled':
      return 'info';
    case 'completed':
      return 'success';
    case 'no_show':
      return 'error';
    case 'rescheduled':
      return 'warning';
    case 'cancelled':
    default:
      return 'default';
  }
};

const formatDateTime = (iso: string, timeZone?: string | null): string => {
  const date = new Date(iso);
  return date.toLocaleString(undefined, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  });
};

const AppointmentCard: FC<AppointmentCardProps> = ({
  appointment,
  onReschedule,
  onCancel,
  onOutcome,
  onViewConversation,
  inlineActions = false,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const failedComms = useMemo(
    () => appointment.communications.filter((c) => c.status === 'failed'),
    [appointment.communications]
  );

  const handleMenuOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const isActionable = appointment.status === 'scheduled';

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        p: 2,
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Icon icon={eventAvailable} width={24} height={24} />
        <Stack spacing={0.5} sx={{ flexGrow: 1 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <Typography variant="subtitle2">
              {appointment.category.name}
            </Typography>
            <Chip
              size="small"
              label={APPOINTMENT_STATUS_LABELS[appointment.status]}
              color={statusChipColor(appointment.status)}
              variant="outlined"
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {formatDateTime(appointment.start_at, appointment.timezone)}
            {' — '}
            {new Date(appointment.end_at).toLocaleTimeString(undefined, {
              hour: 'numeric',
              minute: '2-digit',
              ...(appointment.timezone
                ? { timeZone: appointment.timezone }
                : {}),
            })}
          </Typography>
          <AppointmentUnits units={appointment.units} />
          {appointment.location && (
            <Typography variant="body2" color="text.secondary">
              {appointment.location}
            </Typography>
          )}
          {appointment.notes && (
            <Typography
              variant="body2"
              color="text.secondary"
              fontStyle="italic"
            >
              {appointment.notes}
            </Typography>
          )}
          {appointment.rescheduled_from && (
            <Typography variant="caption" color="text.secondary">
              Rescheduled from an earlier appointment
            </Typography>
          )}
        </Stack>
        {!inlineActions && (
          <Tooltip title="Appointment actions">
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              aria-label="appointment actions"
            >
              <Icon icon={moreVertFill} width={20} height={20} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {failedComms.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {failedComms.length === 1
            ? `One notification was not sent: ${
                COMM_KIND_LABELS[failedComms[0].comm_kind]
              } (${failedComms[0].error_detail ?? 'unknown reason'}).`
            : `${failedComms.length} notifications were not sent — see details below.`}
        </Alert>
      )}

      <CommsPanel
        communications={appointment.communications}
        onViewConversation={onViewConversation}
      />

      {inlineActions && isActionable && (
        <Box component="div" sx={{ display: 'flex', gap: 0.5, mt: 1.5 }}>
          <AppointmentActionBar
            onReschedule={() => onReschedule(appointment)}
            onComplete={() => onOutcome(appointment, 'completed')}
            onNoShow={() => onOutcome(appointment, 'no_show')}
            onCancel={() => onCancel(appointment)}
          />
        </Box>
      )}

      {!inlineActions && (
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          {isActionable && (
            <div>
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  onReschedule(appointment);
                }}
              >
                <ListItemIcon>
                  <Icon icon={refreshFill} width={20} height={20} />
                </ListItemIcon>
                Reschedule
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  onOutcome(appointment, 'completed');
                }}
              >
                <ListItemIcon>
                  <Icon icon={checkmarkOutline} width={20} height={20} />
                </ListItemIcon>
                Complete
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  onOutcome(appointment, 'no_show');
                }}
              >
                <ListItemIcon>
                  <Icon icon={closeCircleOutline} width={20} height={20} />
                </ListItemIcon>
                No-show
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  onCancel(appointment);
                }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon>
                  <Icon icon={closeCircleOutline} width={20} height={20} />
                </ListItemIcon>
                Cancel
              </MenuItem>
            </div>
          )}
          {!isActionable && (
            <MenuItem disabled>
              No actions — appointment is{' '}
              {APPOINTMENT_STATUS_LABELS[appointment.status].toLowerCase()}
            </MenuItem>
          )}
        </Menu>
      )}
    </Box>
  );
};

export default AppointmentCard;
